import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Tv, Trophy, MessageSquare, Star, BarChart3, Smartphone, Zap, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import RollerLabsLogo from "@/components/RollerLabsLogo";
import { useState } from "react";


import staffWatching from "@/assets/staff-watching-leaderboard.jpg";
import mobileLeaderboard from "@/assets/mobile-leaderboard.jpg";
import venueAtmosphere from "@/assets/venue-atmosphere.jpg";
import adminDashboard from "@/assets/admin-dashboard.jpg";
import leaderboardPreview from "@/assets/leaderboard-preview.jpg";
import urbanairLogo from "@/assets/logos/urbanair-logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const MarketingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <RollerLabsLogo size="sm" />
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/get-started")}>Pricing</Button>
            <Button size="sm" onClick={() => navigate("/get-started")}>Get Started <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-24 lg:py-32">
        <div className="container mx-auto px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-xl text-5xl font-extrabold leading-tight tracking-tight lg:text-6xl"
              >
                Turn sales into a game your team{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">wants to win.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mt-6 max-w-lg text-lg text-muted-foreground"
              >
                Built for leisure and attractions venues.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-10 flex flex-col gap-5"
              >
                <div className="flex gap-4">
                  <Button size="lg" onClick={() => navigate("/get-started")} className="px-8 text-base">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate("/display/march-madness")} className="px-8 text-base">
                    See Live Demo
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-primary to-accent">
                    <span className="text-xs font-bold text-primary-foreground">R</span>
                  </div>
                  <span>Built by <span className="font-semibold text-foreground">ROLLER Labs</span></span>
                </div>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="relative"
            >
              <div className="rounded-2xl shadow-2xl border border-border overflow-hidden">
                <img
                  src={leaderboardPreview}
                  alt="Sales leaderboard displayed on a TV screen showing staff rankings"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full rounded-2xl bg-primary/10" />
            </motion.div>
          </div>
        </div>
        <div className="absolute -top-40 left-1/2 -z-10 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
      </section>

      {/* Staff engagement photo strip */}
      <section className="relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src={staffWatching}
            alt="Enthusiastic venue staff pointing at a leaderboard on a TV screen"
            className="w-full h-64 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/60" />
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <p className="text-lg font-semibold text-foreground">Your team, fired up and competing.</p>
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-center text-3xl font-bold">How it works</h2>
          <p className="mx-auto mt-2 max-w-lg text-center text-muted-foreground">
            Go from zero to a live leaderboard on your office TV in under 60 seconds.
          </p>
          <div className="mt-14 grid gap-8 md:grid-cols-4">
            {[
              { step: "1", title: "Connect to ROLLER", desc: "One-click OAuth. Read-only access to products, sales, and staff." },
              { step: "2", title: "Choose products & dates", desc: "Pick which memberships to track and set your campaign window." },
              { step: "3", title: "Pick a template", desc: "Start with a bold, ready-made theme or customise your own." },
              { step: "4", title: "Share the TV link", desc: "Open the unique URL on any TV, monitor, or phone." },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="text-center"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-primary-foreground">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product showcase */}
      <section className="bg-secondary/30 py-20">
        <div className="container mx-auto px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold">Set up in minutes,<br />manage with ease.</h2>
              <p className="mt-4 text-muted-foreground max-w-md">
                The admin dashboard lets you configure campaigns, choose products, set date ranges, and preview your TV display — all from one screen.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {["Live preview as you build", "Drag-and-drop template picker", "Real-time sales data sync", "CSV export for reconciliation"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-foreground">
                    <Zap className="h-4 w-4 text-accent" />{f}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-2xl shadow-xl border border-border overflow-hidden">
                <img src={adminDashboard} alt="Admin dashboard" className="w-full h-auto object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-center text-3xl font-bold">Everything your team needs</h2>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: <Trophy className="h-6 w-6" />, title: "Live ranking & movement", desc: "See who's climbing, who's slipping — updated in real-time." },
              { icon: <Tv className="h-6 w-6" />, title: "Big-screen TV display", desc: "Bold, readable from 3–5 metres. Designed for back-of-house TVs." },
              { icon: <Star className="h-6 w-6" />, title: "Rewards screen", desc: "Show what's up for grabs — gift cards, prizes, bragging rights." },
              { icon: <MessageSquare className="h-6 w-6" />, title: "Manager messages", desc: "Drop a motivational note that rotates onto the display." },
              { icon: <Smartphone className="h-6 w-6" />, title: "Mobile-friendly link", desc: "Staff can check the leaderboard on their phone via a shared link." },
              { icon: <BarChart3 className="h-6 w-6" />, title: "Reporting & reconciliation", desc: "Full sales detail behind every rank. Transparent and auditable." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="rounded-xl border bg-card p-6 transition-shadow hover:shadow-lg"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  {f.icon}
                </div>
                <h3 className="font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile + Venue split section */}
      <section className="relative overflow-hidden bg-secondary/30 py-20">
        <div className="container mx-auto px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <img
                src={mobileLeaderboard}
                alt="Staff member checking leaderboard rankings on their phone"
                className="rounded-2xl shadow-xl max-h-[480px] object-cover border border-border"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold">Check rankings<br />from anywhere.</h2>
              <p className="mt-4 text-muted-foreground max-w-md">
                Staff don't need to be near the TV. Share a link and they can track their position on their phone between shifts, on break, or from home.
              </p>
              <Button className="mt-6" variant="outline" onClick={() => navigate("/display/march-madness")}>
                Try the mobile view <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Venue atmosphere banner */}
      <section className="relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src={venueAtmosphere}
            alt="Vibrant trampoline park venue with guests and staff"
            className="w-full h-64 md:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/40 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-foreground max-w-md">Built for the energy of your venue.</h2>
              <p className="mt-2 text-muted-foreground max-w-sm">Designed to match the pace and excitement of entertainment venues worldwide.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Social proof */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-center text-3xl font-bold">Results that speak</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {[
              { quote: "Membership conversion jumped 15% in our first month. The team loves competing on the leaderboard.", venue: "Trampoline Park — Melbourne", role: "Regional Manager", name: "Sarah Mitchell", avatar: "/avatars/avatar-3.jpg", logo: urbanairLogo },
              { quote: "Staff engagement is up. Managers report more consistent upsells across shifts.", venue: "Entertainment Venue — Austin", role: "Operations Director", name: "James Kowalski", avatar: "/avatars/avatar-5.jpg", logo: urbanairLogo },
            ].map((t, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="rounded-xl border bg-card p-8"
              >
                <img src={t.logo} alt="Venue logo" className="mb-6 h-8 w-auto object-contain" />
                <p className="text-lg font-medium italic">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-4">
                  <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover border-2 border-primary/20" />
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.role} · {t.venue}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-secondary/30 py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">Simple pricing</h2>
          <p className="mt-2 text-muted-foreground">No contracts. Cancel anytime.</p>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mx-auto mt-10 max-w-md rounded-2xl border-2 border-primary bg-card p-10 shadow-lg"
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Leaderboards</p>
            <p className="mt-4 text-5xl font-extrabold">$40<span className="text-xl font-normal text-muted-foreground">/mo</span></p>
            <p className="mt-2 text-muted-foreground">Per venue. Start with a 7-day free trial.</p>
            <ul className="mt-6 space-y-2 text-left text-sm">
              {["7-day free trial — no card required", "Unlimited campaigns", "TV + mobile display", "All templates included", "Reporting & CSV export", "Priority support"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Zap className="h-4 w-4 text-accent" />{f}</li>
              ))}
            </ul>
            <Button className="mt-8 w-full" size="lg" onClick={() => navigate("/get-started")}>
              Start free trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container mx-auto max-w-2xl px-6">
          <h2 className="text-center text-3xl font-bold">FAQ</h2>
          <div className="mt-10 space-y-4">
            <FAQItem q="Does it work on TV?" a="Yes! Just open the display URL in any browser on a TV, monitor, or projector. No hardware or app install required." />
            <FAQItem q="Does it require special hardware?" a="No. Any screen with a web browser works — smart TVs, Chromecasts, Fire Sticks, or a connected laptop." />
            <FAQItem q="What data does it access?" a="Read-only access to Products, Sales/Orders (POS transactions), and Staff/Users. We never modify your ROLLER data." />
            <FAQItem q="Can I run multiple campaigns?" a="Absolutely. Run as many campaigns as you like, each with their own display URL." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t bg-roller-dark py-20 text-center">
        <h2 className="text-3xl font-bold text-primary-foreground">Ready to gamify your sales floor?</h2>
        <p className="mt-3 text-primary-foreground/70">Get your team's leaderboard live in under 60 seconds.</p>
        <Button size="lg" className="mt-8" onClick={() => navigate("/get-started")}>
          Get Started <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto flex items-center justify-between px-6 text-sm text-muted-foreground">
          <RollerLabsLogo size="sm" />
          <p>© 2026 ROLLER Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const FAQItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border bg-card">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between p-5 text-left font-medium">
        {q}
        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm text-muted-foreground">{a}</p>}
    </div>
  );
};

export default MarketingPage;
